/**
 * Single source of truth for the native app ↔ web association.
 *
 * Four places have to agree or deep links silently break:
 *
 * 1. `/.well-known/assetlinks.json`      (Android App Links + credential sharing)
 * 2. `/.well-known/apple-app-site-association` (iOS universal links + autofill)
 * 3. `android/app/src/main/AndroidManifest.xml` (intent filters)
 * 4. `ios/App/App/App.entitlements`      (associated domains)
 *
 * 1 and 2 are checked-in static files under public/.well-known, guarded by
 * association-routes.test.ts so they cannot drift from this module. They were
 * route handlers until their serverless traces made every function oversized
 * enough to fail Vercel's deploy. 3 and 4 are XML and cannot import it,
 * so `app-links.test.ts` parses both and fails when they drift.
 */

export const APP_LINK_HOST = "app.hiive.club";
export const ANDROID_PACKAGE_NAME = "club.hiive.app";
export const CUSTOM_URL_SCHEME = "hiive";

/**
 * SHA-256 of the **Play App Signing** certificate for club.hiive.app, copied
 * from Play Console → Test and release → Setup → App signing.
 *
 * Checked in on purpose: assetlinks.json is a public file, and Google's own
 * flow is to paste this fingerprint into it. Sourcing it from an env var meant
 * a missing Vercel variable served a 404, which fails domain verification and
 * drops every App Link back to the browser.
 *
 * This is the certificate Play re-signs installs with, so it covers every
 * build users get from the store. Internal-testing builds pulled straight off
 * an uploaded AAB are covered by PLAY_UPLOAD_SHA256 below.
 */
export const PLAY_APP_SIGNING_SHA256 =
  "CA:3A:D0:8F:8F:99:06:49:17:F6:B3:F1:59:7F:0E:8D:39:7E:B2:46:3E:67:71:AB:5A:E6:3C:EA:69:E7:50:48";

/**
 * SHA-256 of the **upload** certificate (`~/keys/hiive-upload.jks`), the key
 * this repo signs AABs with before Play re-signs them — Play Console → App
 * signing → Upload key certificate.
 *
 * Published alongside the app-signing cert because Google verifies against
 * whichever key actually signed the installed APK, and that is the upload key
 * for anything sideloaded or installed from an AAB Play has not re-signed.
 * Without it those builds fall back to the browser on every App Link.
 *
 * Checked in for the same reason as PLAY_APP_SIGNING_SHA256: assetlinks.json
 * is public, and a certificate fingerprint is a public identifier — it proves
 * nothing on its own and cannot be used to sign anything. The private
 * keystore, which is the actual secret, stays out of the repo.
 *
 * Local debug certificates still come from `ANDROID_SHA256_FINGERPRINTS` — but
 * note that only affects callers of androidSigningFingerprints(), not the
 * static file that is actually served. See docs/store-submission.md.
 */
export const PLAY_UPLOAD_SHA256 =
  "08:B9:A2:FA:BA:7B:73:D8:39:6D:00:01:5A:B9:26:09:9E:85:AE:38:57:5E:73:FA:EB:F3:C0:A9:09:91:53:1E";

/** Certificates every published assetlinks.json must declare. */
export const MANDATORY_ANDROID_SHA256 = [
  PLAY_APP_SIGNING_SHA256,
  PLAY_UPLOAD_SHA256,
] as const;

export const SHA256_FINGERPRINT_PATTERN =
  /^(?:[A-Fa-f0-9]{2}:){31}[A-Fa-f0-9]{2}$/;

/**
 * Apple Developer Team ID for club.hiive.app, copied from Xcode
 * (`DEVELOPMENT_TEAM` in ios/App/App.xcodeproj).
 *
 * Checked in for the same reason as PLAY_APP_SIGNING_SHA256 above: a Team ID
 * is a public identifier (App Store URLs publish it), and sourcing the AASA
 * team id from an env var meant the well-known file was a serverless function
 * whose trace pulled in every heavy dependency in the app — blowing past
 * Vercel's 150 MB uncompressed function limit and failing the whole deploy.
 * Served as a plain static file from public/ instead.
 */
export const APPLE_DEVELOPER_TEAM_ID = "27H26XC8PK";

/** `TEAMID.club.hiive.app`, the identifier Apple's association files use. */
export const APPLE_APP_ID = `${APPLE_DEVELOPER_TEAM_ID}.${ANDROID_PACKAGE_NAME}`;

/**
 * Paths that open in the app instead of the browser.
 *
 * Two groups:
 *
 * - Invite/action links (/s/, /r/, /invite/, /sessions/join/, /c/, /l/) whose
 *   whole purpose is to hand off to the app.
 * - Public content links (/hiives/, /profile/, /sessions/, /post/,
 *   /activity-post/, /movement/, /weight-training/): tapping a shared community,
 *   session or post link with the app installed must land in the app, not a
 *   browser tab. Claiming them does not hurt SEO or link previews — crawlers
 *   have no app installed, so they still fetch the web HTML, Open Graph tags
 *   and canonicals exactly as before.
 *
 * Every prefix here must resolve to a real route. `/chat/join` was declared for
 * months with no route behind it, which is what Play Console reported as
 * "1 link not working".
 */
export const APP_LINK_PATHS = [
  {
    prefix: "/rewards/",
    route: "src/app/(without-navbar)/rewards/[id]/page.tsx",
    kind: "page",
    comment: "Event Hiive Money rewards open in the app",
  },
  {
    prefix: "/s/",
    route: "src/app/s/[token]/route.ts",
    kind: "redirect",
    comment: "Ticket scan links open the authorised event scanner",
  },
  {
    prefix: "/r/",
    route: "src/app/r/[code]/route.ts",
    kind: "redirect",
    comment: "Short referral links open in the app",
  },
  {
    prefix: "/invite/",
    route: "src/app/(without-navbar)/invite/[code]/page.tsx",
    kind: "page",
    comment: "Referral invite landing pages open in the app",
  },
  {
    prefix: "/sessions/join/",
    route: "src/app/(without-navbar)/sessions/join/[code]/page.tsx",
    kind: "page",
    comment: "Session invite links open in the app",
  },
  {
    prefix: "/c/",
    route: "src/app/c/[code]/route.ts",
    kind: "redirect",
    comment: "Coupon links open in the app so checkout can auto-apply them",
  },
  {
    prefix: "/l/",
    route: "src/app/l/[code]/route.ts",
    kind: "redirect",
    comment: "Short share links open in the app rather than the browser",
  },
  {
    prefix: "/hiives/",
    route: "src/app/(without-navbar)/hiives/[hiiveSlug]/page.tsx",
    kind: "page",
    comment:
      "Community detail, venues, events and review links open in the app",
  },
  {
    prefix: "/profile/",
    route: "src/app/(shell-no-navbar)/profile/[handle]/page.tsx",
    kind: "page",
    comment: "Public profile links open in the app",
  },
  {
    prefix: "/sessions/",
    route: "src/app/(without-navbar)/sessions/[id]/page.tsx",
    kind: "page",
    comment: "Session detail links open in the app",
  },
  {
    prefix: "/post/",
    route: "src/app/(with-navbar)/post/[id]/page.tsx",
    kind: "page",
    comment: "Community post links open in the app",
  },
  {
    prefix: "/activity-post/",
    route: "src/app/(with-navbar)/activity-post/[id]/page.tsx",
    kind: "page",
    comment: "Activity post links open in the app",
  },
  {
    prefix: "/movement/",
    route: "src/app/(mini-apps)/movement/(with-nav)/page.tsx",
    kind: "page",
    comment: "Movement tracker and activity links open in the app",
  },
  {
    prefix: "/weight-training/",
    route: "src/app/(mini-apps)/weight-training/(with-top-nav)/page.tsx",
    kind: "page",
    comment: "Weight-training and exercise links open in the app",
  },
] as const satisfies ReadonlyArray<{
  prefix: string;
  route: string;
  /**
   * "page" is a React route the client router can navigate to.
   * "redirect" is a Route Handler that returns a 3xx plus Set-Cookie.
   */
  kind: "page" | "redirect";
  comment: string;
}>;

/**
 * Paths the native shells never load as the WebView's first document on a
 * cold start: endpoints and assets, not pages. Every other path a link, push
 * tap or widget opens is loaded directly instead of booting `/` → `/hiives`
 * first. Mirrored in HiiveBridgeViewController.swift and MainActivity.java;
 * native-cold-start.test.ts fails when either drifts.
 */
export const NATIVE_START_EXCLUDED_PREFIXES = [
  "/api/",
  "/_next/",
  "/.well-known/",
  "/ingest/",
  "/workflows/",
  "/serwist/",
  "/sw.js",
  "/~offline",
] as const;

/**
 * `hiive://` hosts that are not app routes. The Android home-screen widget's
 * background tap has always sent `hiive://home`, which has no page — so every
 * tap outside its buttons landed on a 404. Mirrored natively (see above).
 */
export const HIIVE_HOST_ALIASES: Readonly<Record<string, string>> = {
  home: "hiives",
};

/**
 * True for claimed paths backed by a Route Handler rather than a page.
 *
 * `router.push("/r/ABCD")` asks the client router for an RSC payload from an
 * endpoint that only ever returns a redirect and a Set-Cookie header, so the
 * referral / promo cookie the whole flow depends on may never be committed.
 * A document navigation resolves the redirect chain exactly as a browser does.
 */
export function requiresDocumentNavigation(pathname: string): boolean {
  // Campaign pushes navigate internally to their per-delivery tracking redirect.
  return (
    pathname.startsWith("/campaign-click/") ||
    APP_LINK_PATHS.some(
      (entry) => entry.kind === "redirect" && pathname.startsWith(entry.prefix),
    )
  );
}

/** Android signing certificates to publish, de-duplicated and validated. */
export function androidSigningFingerprints(
  extra = process.env.ANDROID_SHA256_FINGERPRINTS,
): string[] {
  const parsed = (extra ?? "")
    .split(",")
    .map((entry) => entry.trim().toUpperCase())
    .filter((entry) => SHA256_FINGERPRINT_PATTERN.test(entry));

  return [
    ...new Set([
      ...MANDATORY_ANDROID_SHA256.map((entry) => entry.toUpperCase()),
      ...parsed,
    ]),
  ];
}

/** `TEAMID.club.hiive.app`, the identifier Apple's association files use. */
export function appleAppId(teamId: string): string {
  return `${teamId}.${ANDROID_PACKAGE_NAME}`;
}

/**
 * Rejects Apple's docs placeholder and anything that is not a 10-character
 * Team ID, so a misconfigured deploy 404s rather than poisoning Apple's CDN
 * cache with a file that names the wrong app.
 */
export function isValidAppleTeamId(value: string | undefined): value is string {
  const teamId = value?.trim();
  return !!teamId && teamId !== "TEAM123ABC" && /^[A-Z0-9]{10}$/.test(teamId);
}
